import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import CreateUserForm from './CreateUserForm';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [editingRoles, setEditingRoles] = useState({});
  const [teacherFilter, setTeacherFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const userList = usersSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setUsers(userList);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
    }
  };

  const handleRoleChange = (userId, newRole) => {
    setEditingRoles((prev) => ({ ...prev, [userId]: newRole }));
  };

  const handleSaveRole = async (userId) => {
    const newRole = editingRoles[userId];
    if (!newRole) return;

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { role: newRole });
      alert('✅ Role updated!');
      fetchUsers();
      setEditingRoles((prev) => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    } catch (error) {
      console.error('❌ Error updating role:', error);
    }
  };

  const handleAssignStudent = async (studentId, teacherId) => {
    try {
      const studentRef = doc(db, 'users', studentId);
      await updateDoc(studentRef, { assignedTo: teacherId ? [teacherId] : [] });
      alert('✅ Assignment updated!');
      fetchUsers();
    } catch (error) {
      console.error('❌ Error assigning:', error);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`);
    if (!confirmDelete) return;

    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      alert('🗑️ User record deleted from database.');
      fetchUsers();
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      alert('Failed to delete user.');
    }
  };

  const displayedUsers = users.filter((user) => {
    const matchesTeacher = !teacherFilter || (user.assignedTo && user.assignedTo.includes(teacherFilter));
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesTeacher && matchesSearch;
  });

  const teachers = users.filter((u) => u.role === 'teacher');

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button onClick={() => navigate('/admin')} style={styles.backButton}>← Dashboard</button>
        <h1 style={styles.title}>User Management</h1>
      </header>

      {/* 🛠️ CONTROLS SECTION */}
      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <input 
            type="text" 
            placeholder="Search name or email..." 
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div style={styles.filterWrapper}>
          <label style={styles.label}>Filter by Teacher:</label>
          <select
            style={styles.select}
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
          >
            <option value="">All Teachers</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.email}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 📊 DATA TABLE */}
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>User Info</th>
              <th style={styles.th}>Current Role</th>
              <th style={styles.th}>Teacher / Admin Details</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map((user) => {
              const currentEdit = editingRoles[user.id] || user.role;
              const roleColor = user.role === 'admin' ? '#ef4444' : user.role === 'teacher' ? '#f59e0b' : '#3b82f6';

              return (
                <tr key={user.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.nameText}>{user.name || 'No Name'}</div>
                    <div style={styles.emailText}>{user.email}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={{...styles.roleBadge, backgroundColor: roleColor}}>{user.role}</span>
                    <div style={{marginTop: '8px'}}>
                      <select
                        value={currentEdit}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        style={styles.inlineSelect}
                      >
                        <option value="student">Student</option>
                        <option value="teacher">Teacher</option>
                        <option value="admin">Admin</option>
                      </select>
                      {editingRoles[user.id] && (
                        <button onClick={() => handleSaveRole(user.id)} style={styles.saveBadge}>Save</button>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {user.role === 'student' ? (
                      <select
                        style={styles.inlineSelect}
                        value={(user.assignedTo && user.assignedTo[0]) || ''}
                        onChange={(e) => handleAssignStudent(user.id, e.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {teachers.map((t) => (
                          <option key={t.id} value={t.id}>{t.name || t.email}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={styles.dimText}>N/A for {user.role}s</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionGap}>
                      {user.role === 'student' && (
                        <button
                          style={styles.dashboardButton}
                          onClick={() => navigate(`/admin/student/${user.id}`)}
                        >
                          View
                        </button>
                      )}
                      <button 
                        style={styles.deleteButton} 
                        onClick={() => handleDeleteUser(user.id, user.name || user.email)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayedUsers.length === 0 && (
          <div style={styles.emptyState}>No users found.</div>
        )}
      </div>

      <div style={styles.formSection}>
        <CreateUserForm />
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '40px 20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f8fafc', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' },
  title: { fontSize: '2rem', color: '#1e3a8a', margin: 0, fontFamily: 'Merriweather, serif' },
  backButton: { padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' },
  controls: { display: 'flex', gap: '20px', marginBottom: '20px', backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  searchWrapper: { flex: 2 },
  searchInput: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' },
  filterWrapper: { flex: 1 },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: '#64748b', marginBottom: '5px' },
  select: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' },
  tableCard: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '15px', backgroundColor: '#f1f5f9', color: '#475569', fontSize: '0.9rem', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '15px', verticalAlign: 'middle' },
  nameText: { fontWeight: 'bold', color: '#1e3a8a' },
  emailText: { fontSize: '0.85rem', color: '#64748b' },
  inlineSelect: { padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.9rem' },
  saveBadge: { marginLeft: '8px', padding: '4px 8px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' },
  dashboardButton: { padding: '8px 16px', backgroundColor: '#1e3a8a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' },
  formSection: { marginTop: '40px', padding: '30px', backgroundColor: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1' },
  emptyState: { padding: '40px', textAlign: 'center', color: '#94a3b8' },
  roleBadge: { padding: '2px 8px', borderRadius: '4px', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' },
  deleteButton: { padding: '6px 12px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.2s' },
  actionGap: { display: 'flex', gap: '8px', alignItems: 'center' },
  dimText: { color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }
};

export default UsersManagement;